import { LIST_KINDS, type ListDto, type ListKind } from "@home/shared";
import { useState } from "react";
import { copyList, createList, todayLabel } from "../actions.ts";
import { EmptyState } from "../components/EmptyState.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { KIND_ICONS, kindLabel, t } from "../i18n.ts";
import { api } from "../api.ts";
import { addToShoppingList } from "../components/Fridge.tsx";
import { toast } from "../components/Toast.tsx";
import { sameNameKey } from "../quickAdd.ts";
import { listItems, useSync, visibleLists } from "../store.ts";
import { hapticTap } from "../telegram.ts";
import { useRemote } from "../useRemote.ts";

/** Сколько позиций шаблона показать в подписи. */
const PREVIEW_ITEMS = 4;
/** Сколько названий «Пора докупить» перечислить на карточке. */
const NEED_SHOWN = 6;

/**
 * «Пора докупить» над списками: что закончилось и что по ритму покупок пора брать снова — одной кнопкой
 * в список продуктов (как «🛒 Добавить в список покупок» в боте). Уже лежащее в списках не повторяем.
 */
function NeedCard({ onOpen }: { onOpen: (id: string) => void }) {
  const { state } = useSync();
  const remote = useRemote("suggestions", () => api.pantrySuggestions().then((r) => r.items));
  const inLists = new Set(
    Object.values(state.items)
      .filter((i) => !i.deletedAt && !i.isChecked && state.lists[i.listId] && !state.lists[i.listId]!.isTemplate && !state.lists[i.listId]!.deletedAt)
      .map((i) => sameNameKey(i.name)),
  );
  const need = (remote.data ?? []).filter((s) => s.category !== "health" && !inLists.has(sameNameKey(s.name)));
  if (need.length === 0) return null;
  const add = async () => {
    hapticTap();
    const list = await addToShoppingList(state, need.map((s) => ({ name: s.name, qty: null, unit: null })));
    toast(t("addedToList", { name: list.name }));
    onOpen(list.id);
  };
  return (
    <section className="dash-card">
      <h3>⏳ {t("suggestTitle")}</h3>
      <p className="card-text">
        {need
          .slice(0, NEED_SHOWN)
          .map((s) => s.name)
          .join(", ")}
        {need.length > NEED_SHOWN ? "…" : ""}
      </p>
      <button type="button" className="btn btn-block" onClick={() => void add()}>
        🛒 {t("needToList")}
      </button>
    </section>
  );
}

/** Списки покупок одним экраном: сверху — текущие списки, ниже — «Начать по шаблону». */
export function ListsPage({ onOpen }: { onOpen: (id: string) => void }) {
  const { state, me, status } = useSync();
  const [creating, setCreating] = useState(false);
  const lists = visibleLists(state, false);
  const templates = visibleLists(state, true);

  const useTemplate = async (template: ListDto) => {
    hapticTap();
    onOpen(await copyList(template.id, `${template.name} ${todayLabel()}`, false));
  };

  return (
    <main className="dash">
      <h2 className="page-heading">🛒 {t("listsTitle")}</h2>

      {me && me.role !== "child" && <NeedCard onOpen={onOpen} />}

      {lists.length === 0 && status !== "loading" && (
        <section className="dash-card">
          <EmptyState
            icon="🛒"
            title={t("noListsTitle")}
            text={me?.role === "child" ? t("noListsChild") : t("noLists")}
            action={me ? { label: `＋ ${t("newList")}`, onClick: () => setCreating(true) } : undefined}
          />
        </section>
      )}

      {lists.map((list) => {
        const items = listItems(state, list.id);
        const done = items.filter((i) => i.isChecked).length;
        return (
          <button key={list.id} type="button" className="card" onClick={() => onOpen(list.id)}>
            <span className="card-icon" aria-hidden="true">
              {KIND_ICONS[list.kind]}
            </span>
            <span className="card-body">
              <span className="card-title">
                {list.name}
                {list.visibility === "private" && <span className="badge">🔒 {t("private")}</span>}
              </span>
              <span className="card-meta">
                <span>
                  {done}/{items.length}
                </span>
                <span className="progress">
                  <span style={{ width: items.length ? `${(done / items.length) * 100}%` : 0 }} />
                </span>
              </span>
            </span>
            <span className="chevron" aria-hidden="true">
              ›
            </span>
          </button>
        );
      })}

      {templates.length > 0 && (
        <section>
          <div className="group-title">{t("startFromTemplate")}</div>
          <ul className="row-list group">
            {templates.map((tpl) => {
              const names = listItems(state, tpl.id).map((i) => i.name);
              const preview = names.slice(0, PREVIEW_ITEMS).join(", ") + (names.length > PREVIEW_ITEMS ? "…" : "");
              return (
                <li key={tpl.id} className="row template-row">
                  {/* Нажатие на название — открыть шаблон и поправить состав. */}
                  <button type="button" className="template-open" onClick={() => onOpen(tpl.id)}>
                    <span className="row-icon" aria-hidden="true">
                      {KIND_ICONS[tpl.kind]}
                    </span>
                    <span className="row-body">
                      <span className="row-title">{tpl.name}</span>
                      {preview && <span className="row-meta">{preview}</span>}
                    </span>
                  </button>
                  <button type="button" className="btn btn-small" onClick={() => void useTemplate(tpl)}>
                    {t("start")}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {me && lists.length > 0 && (
        <button className="fab" onClick={() => setCreating(true)}>
          ＋ {t("newList")}
        </button>
      )}

      {creating && (
        <NewListSheet
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            onOpen(id);
          }}
        />
      )}
    </main>
  );
}

function NewListSheet(props: { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ListKind>("grocery");
  const [onlyMe, setOnlyMe] = useState(false);

  const submit = async () => {
    const trimmed = name.trim() || `${kindLabel(kind)} ${todayLabel()}`;
    props.onCreated(await createList(trimmed, kind, onlyMe ? "private" : "family", false));
  };

  return (
    <Sheet title={t("newList")} onClose={props.onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <input className="input" autoFocus placeholder={`${kindLabel(kind)} ${todayLabel()}`} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        <div className="chips" role="radiogroup" aria-label={t("type")}>
          {LIST_KINDS.map((k) => (
            <button type="button" key={k} role="radio" aria-checked={kind === k} className={kind === k ? "chip active" : "chip"} onClick={() => setKind(k)}>
              {KIND_ICONS[k]} {kindLabel(k)}
            </button>
          ))}
        </div>
        <label className="toggle">
          <input type="checkbox" checked={onlyMe} onChange={(e) => setOnlyMe(e.target.checked)} />
          <span>🔒 {t("onlyMe")}</span>
        </label>
        <button className="btn btn-primary" type="submit">
          {t("create")}
        </button>
      </form>
    </Sheet>
  );
}
